import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';
import DeleteWithConfirmation from '../common/DeleteWithConfirmation';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import FormRenderer from '../forms/FormRenderer';

const emptyObject = {};

const TaskDetailPanel = ({ task, onClose, onUpdate, loading = false }) => {
    const detail = task || emptyObject;
    const baseTask = detail.task || detail; // backward compatible
    const formPayload = detail.form || emptyObject;
    const linkedObjects = detail.linked_objects || [];

    const [loadingAction, setLoadingAction] = useState(false);
    const [savingForm, setSavingForm] = useState(false);
    const [formValues, setFormValues] = useState(() => formPayload.data || {});
    const [objectTypes, setObjectTypes] = useState({});
    const [reviewComment, setReviewComment] = useState('');
    const [inlineNotice, setInlineNotice] = useState(null);

    useEffect(() => {
        console.debug('[TaskDetailPanel] detail payload', detail);
        console.debug('[TaskDetailPanel] baseTask', baseTask);
        console.debug('[TaskDetailPanel] formPayload incoming', formPayload);
        setFormValues(formPayload?.data || {});
        setReviewComment('');
        setInlineNotice(null);
    }, [formPayload?.data]);

    useEffect(() => {
        let isActive = true;

        const loadObjectTypes = async () => {
            try {
                const response = await axios.get('/ajax/objecttypes');
                if (!isActive) return;

                const map = {};
                (response?.data || []).forEach((type) => {
                    if (type && type.id !== undefined && type.id !== null) {
                        map[type.id] = type;
                    }
                });

                setObjectTypes(map);
            } catch (error) {
                console.error('Failed to load object types', error);
            }
        };

        loadObjectTypes();

        return () => {
            isActive = false;
        };
    }, []);

    const resolveIconClass = (icon) => {
        if (!icon) return 'fa fa-cube';
        if (icon.startsWith('fa')) return icon;
        if (icon.startsWith('fa-')) return `fa ${icon}`;
        return icon;
    };

    const isCompleted = baseTask?.properties?.completed === 'yes';
    const isReviewTask = baseTask?.properties?.reviewtask == 1;
    const isInReview = baseTask?.properties?.status === 'in review';
    const isRejected = baseTask?.properties?.status === 'rejected';

    const subTasks = baseTask?.sub_tasks || baseTask?.subTasks || [];

    const subTaskIds = useMemo(() => {
        return (subTasks || []).map((t) => t?.id).filter(Boolean);
    }, [subTasks]);

    const filteredLinkedObjects = useMemo(() => {
        if (!Array.isArray(linkedObjects)) return [];
        if (!subTaskIds.length) return linkedObjects;
        return linkedObjects.filter((obj) => !subTaskIds.includes(obj.id));
    }, [linkedObjects, subTaskIds]);

    const fields = useMemo(() => {
        // Support multiple shapes: form.properties (preferred), form.fields, or top-level properties/fields
        if (Array.isArray(formPayload?.form)) {
            console.debug('[TaskDetailPanel] fields source: formPayload.form (array)', formPayload.form);
            return formPayload.form;
        }
        if (Array.isArray(formPayload?.form?.properties)) {
            console.debug('[TaskDetailPanel] fields source: form.form.properties', formPayload.form.properties);
            return formPayload.form.properties;
        }
        if (Array.isArray(formPayload?.form?.fields)) {
            console.debug('[TaskDetailPanel] fields source: form.form.fields', formPayload.form.fields);
            return formPayload.form.fields;
        }
        if (Array.isArray(formPayload?.properties)) {
            console.debug('[TaskDetailPanel] fields source: formPayload.properties', formPayload.properties);
            return formPayload.properties;
        }
        if (Array.isArray(formPayload?.fields)) {
            console.debug('[TaskDetailPanel] fields source: formPayload.fields', formPayload.fields);
            return formPayload.fields;
        }
        console.debug('[TaskDetailPanel] fields source: empty (no array found)', formPayload);
        return [];
    }, [formPayload]);

    const requiredUnfilled = useMemo(() => {
        const result = fields
            .filter((f) => f.required || f.is_required)
            .some((f) => {
                const v = formValues[f.name];
                return v === undefined || v === null || v === '';
            });
        console.debug('[TaskDetailPanel] requiredUnfilled', { result, fields, formValues });
        return result;
    }, [fields, formValues]);

    const handleSaveForm = async () => {
        if (!baseTask?.id) return;
        setSavingForm(true);
        try {
            console.debug('[TaskDetailPanel] saveForm start', { taskId: baseTask.id, formValues });
            await axios.post(API_ENDPOINTS.tasks.saveForm(baseTask.id), formValues);
            onUpdate();
            setInlineNotice({ type: 'success', message: 'Form saved.' });
        } catch (error) {
            console.error('[TaskDetailPanel] saveForm error', error);
            setInlineNotice({ type: 'error', message: error?.response?.data?.error || 'Failed to save form.' });
        } finally {
            setSavingForm(false);
        }
    };

    const submitWithPayload = async (urlBuilder) => {
        setLoadingAction(true);
        try {
            const payload = isReviewTask
                ? { ...formValues, review_comment: reviewComment }
                : formValues;
            console.debug('[TaskDetailPanel] action submit start', { taskId: baseTask.id, payload });
            await axios.post(urlBuilder(baseTask.id), payload);
            onUpdate();
            setInlineNotice({ type: 'success', message: 'Task updated.' });
        } catch (error) {
            console.error('[TaskDetailPanel] action submit error', error);
            setInlineNotice({ type: 'error', message: error?.response?.data?.error || 'Action failed.' });
        } finally {
            setLoadingAction(false);
        }
    };

    const handleComplete = () => submitWithPayload(API_ENDPOINTS.tasks.complete);
    const handlePassReview = () => submitWithPayload(API_ENDPOINTS.tasks.passReview);
    const handleFailReview = () => submitWithPayload(API_ENDPOINTS.tasks.failReview);

    const handleReopen = async () => {
        setLoadingAction(true);
        try {
            await axios.post(API_ENDPOINTS.tasks.reopen(baseTask.id));
            onUpdate();
            setInlineNotice({ type: 'success', message: 'Task reopened.' });
        } catch (error) {
            setInlineNotice({ type: 'error', message: 'Failed to reopen task.' });
        } finally {
            setLoadingAction(false);
        }
    };

    const handleDelete = async () => {
        setLoadingAction(true);
        try {
            await axios.delete(API_ENDPOINTS.tasks.destroy(baseTask.id));
            // Close immediately to avoid rendering a missing-record error after deletion
            if (onClose) onClose();
            if (onUpdate) onUpdate();
        } catch (error) {
            setInlineNotice({ type: 'error', message: 'Failed to delete task.' });
        } finally {
            setLoadingAction(false);
        }
    };

    const statusLabel = isCompleted ? 'Completed' : (isInReview ? 'In Review' : (isRejected ? 'Rejected' : 'In Progress'));
    const statusClass = isCompleted ? 'is-complete' : (isInReview ? 'is-review' : (isRejected ? 'is-rejected' : 'is-progress'));
    const dueDateLabel = baseTask.date_due ? new Date(baseTask.date_due).toLocaleDateString() : 'No due date';

    const actionButtons = () => {
        if (isCompleted) {
            return (
                <button
                    onClick={handleReopen}
                    disabled={loadingAction}
                    className="btn btn-warning btn-sm"
                >
                    {loadingAction ? 'Processing…' : 'Reopen Task'}
                </button>
            );
        }

        if (isReviewTask) {
            return (
                <div className="btn-group btn-group-justified" role="group">
                    <div className="btn-group" role="group">
                        <button
                            onClick={handlePassReview}
                            disabled={loadingAction}
                            className="btn btn-success btn-sm"
                        >
                            {loadingAction ? '…' : 'Pass'}
                        </button>
                    </div>
                    <div className="btn-group" role="group">
                        <button
                            onClick={handleFailReview}
                            disabled={loadingAction}
                            className="btn btn-danger btn-sm"
                        >
                            {loadingAction ? '…' : 'Reject'}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <button
                onClick={handleComplete}
                disabled={loadingAction || (fields.length > 0 && requiredUnfilled)}
                className="btn btn-success btn-sm"
            >
                {loadingAction ? 'Processing…' : 'Complete Task'}
            </button>
        );
    };

    return (
        <div className="task-detail-card">
            <div className="task-detail-body">
                {inlineNotice && (
                    <div className={`alert alert-${inlineNotice.type === 'error' ? 'danger' : (inlineNotice.type === 'success' ? 'success' : 'info')} task-detail-alert`}>
                        {inlineNotice.message}
                    </div>
                )}

                {loading && (
                    <div className="alert alert-info task-detail-alert">
                        Loading full task details…
                    </div>
                )}

                <div className="task-detail-grid">
                    <div className="task-detail-meta">
                        <span className="task-detail-label">Status</span>
                        <span className={`task-detail-status-pill ${statusClass}`}>{statusLabel}</span>
                    </div>
                    <div className="task-detail-meta">
                        <span className="task-detail-label">Due Date</span>
                        <div className="task-detail-meta-box">
                            <i className="fa fa-calendar text-muted" aria-hidden="true" />
                            <span>{dueDateLabel}</span>
                        </div>
                    </div>
                </div>

                <div className="task-detail-section">
                    <span className="task-detail-label">Description</span>
                    <div className="task-detail-description">
                        {baseTask.description ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                                {baseTask.description}
                            </ReactMarkdown>
                        ) : (
                            'No description provided.'
                        )}
                    </div>
                </div>

                {fields.length > 0 && (
                    <div className="task-detail-form-card">
                        <div className="task-detail-form-header">
                            <div>
                                <div className="task-detail-kicker">Task Form</div>
                                <div className="task-detail-form-subtext">
                                    {formPayload.readonly ? 'Read only' : 'Update the structured data for this task.'}
                                </div>
                            </div>
                            <div className="task-detail-form-actions">
                                {requiredUnfilled && !isCompleted && (
                                    <span className="task-detail-alert-pill">Required fields incomplete</span>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={handleSaveForm}
                                    disabled={savingForm || !!formPayload.readonly}
                                >
                                    {savingForm ? 'Saving…' : 'Save Form'}
                                </button>
                            </div>
                        </div>
                        <div className="task-detail-form-body">
                            <FormRenderer
                                form={formPayload.form || { properties: fields }}
                                initialValues={formValues}
                                readOnly={!!formPayload.readonly}
                                onChange={(vals) => setFormValues(vals)}
                            />
                        </div>
                    </div>
                )}

                {isReviewTask && (
                    <div className="task-detail-section">
                        <label className="task-detail-label">Reviewer Reply</label>
                        <textarea
                            className="form-control"
                            placeholder="Share your feedback or questions for the task owner"
                            rows={3}
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                        />
                    </div>
                )}

                {!loading && fields.length === 0 && (
                    <div className="alert alert-warning task-detail-alert">
                        No form is configured for this task.
                    </div>
                )}

                {filteredLinkedObjects.length > 0 && (
                    <div className="task-detail-list-card">
                        <div className="task-detail-list-header">
                            <strong>Linked Objects</strong>
                        </div>
                        <ul className="list-group">
                            {filteredLinkedObjects.map((obj) => {
                                const typeMeta = objectTypes[obj.type_id] || null;
                                const iconClass = resolveIconClass(typeMeta?.icon);
                                const typeLabel = typeMeta?.title || obj.type_id || '?';

                                return (
                                    <li key={obj.id} className="list-group-item task-detail-list-item">
                                        <div className="task-detail-list-row">
                                            <div className="task-detail-list-main">
                                                <i className={iconClass} aria-hidden="true" />
                                                <span className="label label-primary task-detail-list-badge">{typeLabel}</span>
                                                <div>
                                                    <div className="task-detail-list-title">{obj.title || 'Untitled'}</div>
                                                    <div className="task-detail-list-subtext">
                                                        ID {obj.id}{obj.tid ? ` · TID ${obj.tid}` : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="task-detail-list-subtext">
                                                {obj.object_definitions_id ? `Def ${obj.object_definitions_id}` : ''}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {filteredLinkedObjects.length === 0 && !loading && (
                    <div className="alert alert-warning task-detail-alert">
                        No linked objects for this task.
                    </div>
                )}

                {subTasks && subTasks.length > 0 && (
                    <div className="task-detail-list-card">
                        <div className="task-detail-list-header">
                            <strong>Sub-tasks</strong>
                        </div>
                        <ul className="list-group">
                            {subTasks.map((child) => {
                                const statusValue = (child.properties?.status || '').toLowerCase();
                                const isChildCompleted = child.properties?.completed === 'yes';
                                const isChildReviewTask = child.properties?.reviewtask == 1;
                                const isChildRejected = statusValue === 'rejected';
                                const isChildInReview = statusValue === 'in review' || statusValue === 'in_review';
                                const statusLabelChild = isChildCompleted
                                    ? 'Completed'
                                    : isChildRejected
                                        ? 'Review Failed'
                                        : isChildInReview
                                            ? 'In Review'
                                            : 'In Progress';
                                const statusClassChild = isChildCompleted
                                    ? 'is-complete'
                                    : isChildRejected
                                        ? 'is-rejected'
                                        : isChildInReview
                                            ? 'is-review'
                                            : 'is-progress';
                                const reviewResponse = child.properties?.review_comment
                                    || child.properties?.review_response
                                    || child.properties?.reviewer_response
                                    || '';
                                const linkHref = child.id ? `/tasks/${child.id}` : undefined;

                                const renderStatusIcon = () => {
                                    if (isChildRejected) return <i className="fa fa-times-circle text-danger" aria-hidden="true"></i>;
                                    if (isChildCompleted) return <i className="fa fa-check-circle text-success" aria-hidden="true"></i>;
                                    if (isChildInReview) return <i className="fa fa-hourglass-half text-info" aria-hidden="true"></i>;
                                    if (isChildReviewTask) return <i className="fa fa-search text-muted" aria-hidden="true"></i>;
                                    return <i className="fa fa-circle-o text-muted" aria-hidden="true"></i>;
                                };

                                return (
                                    <li key={child.id} className="list-group-item task-detail-list-item">
                                        <div className="task-detail-list-row">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <a
                                                    href={linkHref}
                                                    className="task-detail-list-title"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {child.title}
                                                </a>
                                                {isChildReviewTask && reviewResponse && (
                                                    <div className="task-detail-list-subtext">Reviewer: {reviewResponse}</div>
                                                )}
                                            </div>
                                            <span className={`task-detail-status-pill ${statusClassChild}`}>
                                                {renderStatusIcon()} {statusLabelChild}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                <div className="task-detail-actions">
                    <div>{actionButtons()}</div>
                    <DeleteWithConfirmation
                        onDelete={handleDelete}
                        label="Delete Task"
                        confirmLabel="Confirm"
                        cancelLabel="Cancel"
                        className="btn btn-default btn-sm task-detail-delete"
                        disabled={loadingAction}
                        isProcessing={loadingAction}
                    />
                </div>
            </div>
        </div>
    );
};

export default TaskDetailPanel;
